import { useState, useEffect, useCallback, useRef } from "react";
import { authFetch } from "../../../../utils/authFetch";

const previewUrl = (problemNumber) =>
  `/api/v1/problems/${encodeURIComponent(problemNumber)}/communications/preview`;

const GENERIC_ERROR = "Unable to load the Slack preview.";

// Debounces the fetch itself (not just the abort), so typing in any Communications-tab field
// doesn't post to the preview endpoint once per keystroke -- both the Preview modal's own call
// and ProblemForm's eager Subject-seeding call share this. retry() bypasses it (an explicit
// Retry click should fire immediately, not wait out the window).
const PREVIEW_DEBOUNCE_MS = 300;

// useSlackCardPreview posts a draft communication to the preview endpoint and returns the
// card RenderCard would send. Keyed off JSON.stringify(payload), not the payload's object
// identity -- buildCurrentSendPayload() returns a fresh object on every parent render.
export const useSlackCardPreview = (problemNumber, outageNumber, payload) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const payloadKey = JSON.stringify(payload);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (!problemNumber || !outageNumber) {
      setLoading(false);
      setCard(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(previewUrl(problemNumber), {
        method: "POST",
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!response || !response.ok) {
        let message = GENERIC_ERROR;
        try {
          const errBody = await response.json();
          message = errBody?.error?.message || message;
        } catch {
          // no JSON body to read -- keep the generic message
        }
        throw new Error(message);
      }
      const body = await response.json();
      if (controller.signal.aborted) return;
      setCard(body.card || null);
    } catch (err) {
      if (err.name === "AbortError" || controller.signal.aborted) return;
      setCard(null);
      setError(err.message || GENERIC_ERROR);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemNumber, outageNumber, payloadKey]);

  useEffect(() => {
    const timer = setTimeout(load, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      abortRef.current?.abort();
    };
  }, [load]);

  return { card, loading, error, retry: load };
};
